import { createContext, useContext } from 'react';
import type { KeycloakInstance } from 'keycloak-js';

export type User = {
  id: string;
  username: string;
  givenName: string;
  familyName: string;
  name: string;
  email?: string;
};

export type PermissionListType = {
  uid: string;
  id: string;
};

export type AuthData = {
  keycloak?: KeycloakInstance;
  isAuthenticated: boolean;
  token: string | null;
  isAdmin: boolean;
  user: User | null;
  groups: string[];
  roles: string[];
  canUseElevatedPermissions: boolean;
  useElevatedPermissions: boolean;
  setUseElevatedPermissions: (useElevatedPermissions: boolean) => void;
  apiHeaders: { [key: string]: string };
  permissionToken: string | null;
  permissionList: PermissionListType[] | null;
  setPermissionToken: (permissionToken: string | null) => void;
};

export type AuthActions = {
  logIn: KeycloakInstance['login'];
  logOut: KeycloakInstance['logout'];
};

export type AuthContract = AuthData & {
  actions: AuthActions;
};

const emptyKeyCloakAuthFunction = (): any => Promise.resolve();

export const defaultContextValue = {
  keycloak: undefined,
  isAuthenticated: false,
  token: null,
  isAdmin: false,
  user: null,
  roles: [],
  groups: [],
  actions: {
    logIn: emptyKeyCloakAuthFunction,
    logOut: emptyKeyCloakAuthFunction,
  },
  canUseElevatedPermissions: false,
  useElevatedPermissions: false,
  setUseElevatedPermissions: () => {},
  apiHeaders: {},
  permissionToken: null,
  permissionList: null,
  setPermissionToken: () => {},
};

const AuthContext = createContext<AuthContract>({
  ...defaultContextValue,
});

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
